import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import {Alert, AlertTitle, Box, Button, Divider, Typography} from "@mui/material";
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import InventoryAvailabilityThresholds from "../../../inventory/availability-thresholds";
import {ProductOtherPartnerAvailabilityCard, ProductWarehouseCard} from "../../../../../../components/app-specific/product-cards/product-warehouse-card";


/**
 * @param {{ id?: number, code?: string, name?: string }} spireWarehouse
 */
const buildFallbackLocalWarehouse = (spireWarehouse) => ({
    warehouseId: spireWarehouse?.id,
    warehouseCode: spireWarehouse?.code ?? '',
    warehouseName: spireWarehouse?.name ?? '',
    isComingSoon: false,
    availableQty: 0,
    committedQty: 0,
    onHandQty: 0,
    backOrderQty: 0,
    onPurchaseQty: 0,
});

const ProductInformationAvailabilityView = ({loading: loadingProps, partNo, partId, isSerializable, cost}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.productSingleAvailability'});
    const [warehouses, setWarehouses] = useState([]);
    const [shippingCompanies, setShippingCompanies] = useState([]);
    const [otherPartners, setOtherPartners] = useState([]);
    const [schedules, setSchedules] = useState({});
    const [loadingAvailability, setLoadingAvailability] = useState(true);
    const [degradedAvailability, setDegradedAvailability] = useState(false);
    const [availabilityErrorMessage, setAvailabilityErrorMessage] = useState('');
    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();

    const loading = useMemo(() => loadingProps || loadingAvailability, [loadingProps, loadingAvailability])

    /**
     * Fetches the warehouse schedules from the server and if the result of the api is successful, sets the state.
     * @type {(function(*): Promise<void>)|*}
     */
    const getWarehouseSchedules = useCallback(async (warehouseIds) => {
        const response = await Api.getPartnerOperationHours({warehouseIds: warehouseIds});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const allItems = response.data || [];
            if (!allItems.length) return;

            // Group by warehouseId
            const nextSchedules = {};
            for (const item of allItems) {
                if (!nextSchedules[item.warehouseId])
                    nextSchedules[item.warehouseId] = [];
                nextSchedules[item.warehouseId].push(item);
            }
            setSchedules(nextSchedules);
        }
    }, [isMounted])


    /**
     * Fetches the part warehouse information from the server and if the result of the api is successful, sets the state.
     * @type {(function(): Promise<*>)|*}
     */
    const getPartWarehouseInformation = useCallback(async () => {
        const response = await Api.getPartWarehouseInformationForPart(partId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setWarehouses(prevState => {
                return prevState.map(warehouse => {
                    const foundInfo = response.data?.find(e => e.warehouseId === warehouse.localWarehouse.warehouseId);
                    if (!foundInfo)
                        return warehouse;
                    return {
                        ...warehouse,
                        localWarehouse: {
                            ...warehouse.localWarehouse,
                            thresholdEnabled: foundInfo.minimumOrder !== null || foundInfo.reOrderPoint !== null,
                            thresholdMinimumOrderQuantity: foundInfo.minimumOrder,
                            thresholdReorderPoint: foundInfo.reOrderPoint,
                        }
                    }
                })
            });
        }
    }, [isMounted, partId])

    /**
     * Fetches the warehouses and availabilities information of the current product from the server.
     * @return {Promise<void>}
     */
    const getProductAvailability = useCallback(async (silent = false) => {
        if (!partNo)
            return
        if (!silent) {
            setLoadingAvailability(true);
            setDegradedAvailability(false);
            setAvailabilityErrorMessage('');
        }
        const [availabilityResponse, warehousesResponse] = await Promise.all([
            Api.getProductAvailabilitiesV2({partNumbers: [partNo]}),
            Api.getOperatorWarehouses(),
        ])
        if (!isMounted())
            return;
        if (!silent) {
            setLoadingAvailability(false);
        }

        const warehousesOk = Boolean(warehousesResponse?.resultFlag);
        const availabilityOk = Boolean(availabilityResponse?.resultFlag);

        if (warehousesOk && availabilityOk) {
            const data = availabilityResponse.data?.[0];
            setDegradedAvailability(false);
            setAvailabilityErrorMessage('');
            setWarehouses(data?.localWarehouses?.map(e => ({localWarehouse: e, spireWarehouse: warehousesResponse.data?.find(l => l.id === e.warehouseId)})) ?? [])
            setOtherPartners(data?.partners?.filter(e => !e.isMainPartner) ?? []);

            if (operatorAccessLevels.packages.sales.warehouseSchedules) {
                const allWarehouseIds = Array.from(new Set(data?.partners?.map(e => e.warehouses)?.flat()?.map(w => w.id) ?? [])).filter(Boolean);
                if (!!allWarehouseIds.length) {
                    void getWarehouseSchedules(allWarehouseIds);
                }
            }
            if (operatorAccessLevels.packages.bizInventory.viewAvailabilityThresholds) {
                void getPartWarehouseInformation();
            }
            return;
        }

        if (warehousesOk && !availabilityOk) {
            const message = typeof availabilityResponse?.message === 'string'
                ? availabilityResponse.message.trim()
                : '';
            setDegradedAvailability(true);
            setAvailabilityErrorMessage(message);
            setOtherPartners([]);
            setSchedules({});
            const spireList = (warehousesResponse.data ?? []).filter((w) => w?.id != null);
            setWarehouses(spireList.map(spireWarehouse => ({
                localWarehouse: buildFallbackLocalWarehouse(spireWarehouse),
                spireWarehouse,
            })));
            return;
        }

        setDegradedAvailability(false);
        setAvailabilityErrorMessage('');
        setWarehouses([]);
        setOtherPartners([]);
    }, [partNo, isMounted, operatorAccessLevels.packages.sales.warehouseSchedules, operatorAccessLevels.packages.bizInventory.viewAvailabilityThresholds, getWarehouseSchedules, getPartWarehouseInformation])

    /**
     * With each change in id of the url params:
     * - fetches the warehouses and availabilities information of its respective product.
     */
    useEffect(() => {
        if (loadingProps) return;
        getProductAvailability().then()
    }, [getProductAvailability, loadingProps])

    /**
     * Refreshes the warehouses and availabilities information of the current product.
     * @type {(function(): void)|*}
     */
    const refreshWarehouses = useCallback(async () => {
        await getProductAvailability(true);
    }, [getProductAvailability])

    /**
     * Retries to fetch the warehouses and availabilities information of the current product in case of degraded availability.
     * @type {(function(): Promise<void>)|*}
     */
    const retryAvailability = useCallback(async () => {
        await getProductAvailability(false);
    }, [getProductAvailability])

    /**
     * Fetches the shipping companies from the server and if the result of the api is successful, sets the state.
     * @type {(function(): Promise<void>)}
     */
    const getShippingCompanies = useCallback(async () => {
        const response = await Api.getFreightCompanies({});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setShippingCompanies(response?.data ?? []);
        }
    }, [isMounted])

    /**
     * As soon as the component mounts, it fetches the shipping companies.
     */
    useEffect(() => {
        void getShippingCompanies();
    }, [getShippingCompanies])


    const warehouseAvailabilityForThresholds = useMemo(() => Object.fromEntries(warehouses.map(e => ([`${partNo}____${e.localWarehouse.warehouseId}`, e.localWarehouse?.availableQty ?? 0,]))), [partNo, warehouses]);

    const partInfo = useMemo(() => ({partNo, partId, isSerializable, cost}), [partNo, partId, isSerializable, cost]);
    const augmentedWarehouses = useMemo(() => warehouses.map(warehouse => ({
        ...warehouse,
        setWarehouse: (updater) => {
            setWarehouses(prevState => {
                const found = prevState.find(w => w.localWarehouse.warehouseId === warehouse.localWarehouse.warehouseId);
                if (!found)
                    return prevState;
                const updatedWarehouse = typeof updater === 'function' ? updater(found) : updater;
                return prevState.map(w => w.localWarehouse.warehouseId === warehouse.localWarehouse.warehouseId ? updatedWarehouse : w);
            })
        },
        setSchedule: (warehouseId, updater) => {
            setSchedules(prevState => {
                const found = prevState[warehouseId];
                const updatedSchedules = typeof updater === 'function' ? updater(found ?? []) : updater;
                return {...(prevState ?? {}), [warehouseId]: updatedSchedules};
            })
        }
    })), [warehouses]);

    const degradedAlertBody = degradedAvailability
        ? (availabilityErrorMessage || t('degradedFallbackMessage'))
        : '';

    return (
        <>
            <div className={'product-availability-view part-info-paper d-flex flex-column p-3'}>
                {
                    loading &&
                    <>
                        <div className='my-2 w-100'/>
                        <LoadingLottieAnimation className={''} width={'80%'}/>
                        <div className='my-2 w-100'/>
                    </>
                }
                {
                    !loading &&
                    <>
                        {
                            degradedAvailability &&
                            <Alert
                                severity={'warning'}
                                sx={{marginBlockEnd: 2}}
                                action={
                                    <Button
                                        color={'inherit'}
                                        size={'small'}
                                        onClick={() => void retryAvailability()}
                                        disabled={loadingAvailability}
                                    >
                                        {t('retry')}
                                    </Button>
                                }
                            >
                                <AlertTitle>{t('degradedTitle')}</AlertTitle>
                                {degradedAlertBody}
                            </Alert>
                        }
                        <div className={'d-flex flex-wrap'} style={{gap: 12}}>
                            {
                                augmentedWarehouses.map(warehouse =>
                                    <ProductWarehouseCard
                                        key={warehouse.localWarehouse.warehouseId}
                                        localWarehouse={warehouse.localWarehouse}
                                        spireWarehouse={warehouse.spireWarehouse}
                                        setWarehouse={warehouse.setWarehouse}
                                        refreshWarehouses={refreshWarehouses}
                                        shippingCompanies={shippingCompanies}
                                        partInfo={partInfo}
                                        schedule={schedules?.[warehouse.localWarehouse.warehouseId]}
                                        setSchedule={warehouse.setSchedule}
                                        viewOnly={degradedAvailability}
                                    />
                                )
                            }
                        </div>
                        {
                            !!otherPartners.length &&
                            <Box sx={{position: 'relative', marginBlockEnd: 4, marginBlockStart: 7}}>
                                <Divider absolute sx={{
                                    borderStyle: "dashed",
                                    borderColor: 'text.disabled',
                                    borderBottomWidth: 2,
                                    paddingInline: 3,
                                }}/>
                                <Typography
                                    variant={'body2'}
                                    color={'tertiary.main'}
                                    fontWeight={600}
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -65%)',
                                        backgroundColor: 'background.paper',
                                        paddingInline: 2,
                                    }}
                                >
                                    Other Partners' Warehouses
                                </Typography>
                            </Box>
                        }
                        <div className={'d-flex flex-column'}>
                            {
                                otherPartners.map(partner =>
                                    <ProductOtherPartnerAvailabilityCard
                                        key={partner.partnerName}
                                        partner={partner}
                                        schedules={schedules}
                                    />
                                )
                            }
                        </div>
                    </>
                }
                {
                    operatorAccessLevels.packages.bizInventory.viewAvailabilityThresholds &&
                    !degradedAvailability &&
                    <>
                        <Box sx={{position: 'relative', marginBlockEnd: 4, marginBlockStart: 7}}>
                            <Divider absolute sx={{
                                borderStyle: "dashed",
                                borderColor: 'text.disabled',
                                borderBottomWidth: 2,
                                paddingInline: 3,
                            }}/>
                            <Typography
                                variant={'body2'}
                                color={'tertiary.main'}
                                fontWeight={600}
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -65%)',
                                    backgroundColor: 'background.paper',
                                    paddingInline: 2,
                                }}
                            >
                                Existing Thresholds Alerts
                            </Typography>
                        </Box>
                        <InventoryAvailabilityThresholds
                            partNo={partNo}
                            disallowNavigation
                            availability={warehouseAvailabilityForThresholds}
                        />
                    </>
                }
            </div>
        </>
    )
}

export default ProductInformationAvailabilityView;


